import { orderBy } from "lodash-es";
import { useSnackbar } from "notistack";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";

import { ExtendedWMSParams, FilterTag, Layer, LayerSet } from "types/types";
import { get } from "utils/AxiosUtils";
import { deserializeErrors } from "utils/ErrorUtils";
import { splitString } from "utils/StringUtilts";

import {
  useFilterTags,
  useUrlFilterTags,
  useUrlSearch,
  useUrlSort,
} from "./filterHooks";

export function useLayerSets() {
  const { enqueueSnackbar } = useSnackbar();
  async function fetch(): Promise<LayerSet[]> {
    const { data } = await get<LayerSet[]>(`/api/layersets`);
    return data;
  }
  return useQuery(["layers"], fetch, {
    onError: (err) => {
      const errors = deserializeErrors(err);
      errors.map((er) => {
        return enqueueSnackbar(er.message, {
          variant: "error",
          preventDuplicate: true,
        });
      });
    },
    cacheTime: Infinity,
    staleTime: Infinity,
  });
}

export function useFilteredLayerSets(layerSets: LayerSet[]) {
  const { tagIds } = useUrlFilterTags();
  const { data: tags } = useFilterTags();
  if (tagIds.length === 0) return layerSets;

  const tagSelection = createListWithChildrenIds(
    tagIds,
    tags?.originalTags || []
  );

  return layerSets.filter((layerSet) =>
    isLayerSetSelected(
      layerSet.tagList.map((tag) => tag.id),
      tagSelection
    )
  );
}

function createListWithChildrenIds(tagIds: number[], tags: FilterTag[]) {
  const completeColectionTagIds = tagIds.reduce(
    (ids: number[][], id: number) => {
      const firstSetOfChildren = tags
        .filter((tag) => tag.parentId === id)
        .map((child) => child.id);
      const children = gatherChildrenIds(
        tags,
        firstSetOfChildren,
        firstSetOfChildren
      );
      return [...ids, [...children, id]];
    },
    []
  );
  return completeColectionTagIds;
}

function gatherChildrenIds(
  tags: FilterTag[],
  ids: number[],
  idCollection: number[]
): number[] {
  const [...newIds] = idCollection
    .map((i) => tags.filter((tag) => tag.parentId === i))
    .flat()
    .map((i) => i.id);

  if (newIds.length === 0) return [...ids, ...newIds];
  return gatherChildrenIds(tags, [...ids, ...newIds], newIds);
}

function isLayerSetSelected(layerSetTags: number[], selectedTags: number[][]) {
  const isSelected = selectedTags.every((array) => {
    const isTrue = array.some((i) => {
      return layerSetTags.includes(i);
    });
    return isTrue;
  });
  return isSelected;
}

export function useFilteredByStringLayerSets(layerSets: LayerSet[]) {
  const { search } = useUrlSearch();

  return layerSets.filter((layerSet) => {
    return (
      layerSet.name.toLowerCase().includes(search.toLowerCase()) ||
      splitString(layerSet.description).some(
        (word) => word.toLowerCase() === search.toLowerCase()
      ) ||
      layerSet.description.toLowerCase().includes(search.toLowerCase())
    );
  });
}

export function useSortedLayerSets(layerSets: LayerSet[]) {
  const { selectedSortOption } = useUrlSort();
  if (selectedSortOption.value === "asc") {
    return orderBy(layerSets, ["'name"], ["asc"]);
  }
  if (selectedSortOption.value === "desc") {
    return orderBy(layerSets, ["name"], ["desc"]);
  }
  return layerSets;
}

export function useUrlSelectedLayerSet() {
  const [params, setParams] = useSearchParams();
  const urlLayerSetId = +(params.get("id") || "");

  function selectLayerSet(id: number) {
    const newParams = new URLSearchParams(params);
    newParams.set("id", id.toString());
    setParams(newParams);
  }

  function deselectLayerSet() {
    const newParams = new URLSearchParams(params);
    newParams.delete("id");
    newParams.delete("layer");
    setParams(newParams);
  }

  return { urlLayerSetId, selectLayerSet, deselectLayerSet };
}

export function useSelectedLayerSet(layerSetId: number, layerSets: LayerSet[]) {
  const { data: selectedLayerSet } = useQuery(
    ["layers", layerSetId],
    () => {
      return layerSets.find((layerSet) => layerSet.id === layerSetId);
    },
    {
      enabled: layerSetId !== 0,
      cacheTime: Infinity,
      staleTime: Infinity,
    }
  );
  return selectedLayerSet;
}

export function useUrlSelectLayer(layerList: Layer[]) {
  const [params, setParams] = useSearchParams();
  const urlLayerSetId = +(params.get("layer") || "");

  const [firstLayer] = layerList;
  const defaultLayer = layerList.find((layer) => layer.defaultSelection);
  const foundLayer = layerList.find((layer) => layer.id === urlLayerSetId);
  const selectedLayer = foundLayer || defaultLayer || firstLayer;

  function selectLayer(id: number) {
    const newParams = new URLSearchParams(params);
    newParams.set("layer", id.toString());
    setParams(newParams);
  }

  return { selectedLayer, selectLayer };
}

export function useWMSProperties(layerName: string): ExtendedWMSParams {
  return {
    layers: layerName,
    format: "image/png",
    transparent: true,
    tiled: true,
    opacity: 0.8,
    url: "/geoserver/wms",
  };
}
